'use client';

import { privacyConfig } from '@/features/privacy/privacy.config';
import { MainLayout } from '@/layouts/main';
import { useT } from 'next-i18next/client';
import { generateNextSeo } from 'next-seo/pages';
import Head from 'next/head';
import type { FC } from 'react';

type PrivacyPageProps = {
  locale: string;
};

export const PrivacyPage: FC<PrivacyPageProps> = ({ locale }) => {
  const { t } = useT(privacyConfig.i18nNamespaces);

  return (
    <>
      <Head>
        {generateNextSeo({
          title: `${t('privacy:page.title')} - ${locale}`,
          description: t('privacy:page.description'),
        })}
      </Head>
      <MainLayout>
        <div className="bg-[#0A0A0A]">
          <div className="mx-auto max-w-4xl px-4 py-8">
            <div className="mb-8 text-center">
              <h1 className="text-4xl font-bold text-white md:text-5xl">
                {t('privacy:page.title')}
              </h1>
              <p className="mt-2 text-sm text-gray-400 italic">
                {t('privacy:lastUpdated')}
              </p>
            </div>
            <div className="space-y-8">
              {/* Section 1 */}
              <section className="space-y-4">
                <h2 className="text-2xl font-bold text-white">
                  {t('privacy:section_01_title')}
                </h2>
                <p className="leading-relaxed text-gray-300">
                  {t('privacy:section_01_intro')}
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start text-gray-300">
                    <span className="mr-2 text-white">•</span>
                    {t('privacy:section_01_list_0')}
                  </li>
                  <li className="flex items-start text-gray-300">
                    <span className="mr-2 text-white">•</span>
                    {t('privacy:section_01_list_1')}
                  </li>
                  <li className="flex items-start text-gray-300">
                    <span className="mr-2 text-white">•</span>
                    {t('privacy:section_01_list_2')}
                  </li>
                  <li className="flex items-start text-gray-300">
                    <span className="mr-2 text-white">•</span>
                    {t('privacy:section_01_list_3')}
                  </li>
                </ul>
              </section>

              {/* Section 2 */}
              <section className="space-y-4">
                <h2 className="text-2xl font-bold text-white">
                  {t('privacy:section_02_title')}
                </h2>
                <p className="leading-relaxed text-gray-300">
                  {t('privacy:section_02_intro')}
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start text-gray-300">
                    <span className="mr-2 text-white">•</span>
                    {t('privacy:section_02_list_0')}
                  </li>
                  <li className="flex items-start text-gray-300">
                    <span className="mr-2 text-white">•</span>
                    {t('privacy:section_02_list_1')}
                  </li>
                  <li className="flex items-start text-gray-300">
                    <span className="mr-2 text-white">•</span>
                    {t('privacy:section_02_list_2')}
                  </li>
                  <li className="flex items-start text-gray-300">
                    <span className="mr-2 text-white">•</span>
                    {t('privacy:section_02_list_3')}
                  </li>
                </ul>
              </section>

              {/* Section 3 */}
              <section className="space-y-4">
                <h2 className="text-2xl font-bold text-white">
                  {t('privacy:section_03_title')}
                </h2>
                <p className="leading-relaxed text-gray-300">
                  {t('privacy:section_03_intro')}
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start text-gray-300">
                    <span className="mr-2 text-white">•</span>
                    {t('privacy:section_03_list_0')}
                  </li>
                  <li className="flex items-start text-gray-300">
                    <span className="mr-2 text-white">•</span>
                    {t('privacy:section_03_list_1')}
                  </li>
                  <li className="flex items-start text-gray-300">
                    <span className="mr-2 text-white">•</span>
                    {t('privacy:section_03_list_2')}
                  </li>
                  <li className="flex items-start text-gray-300">
                    <span className="mr-2 text-white">•</span>
                    {t('privacy:section_03_list_3')}
                  </li>
                  <li className="flex items-start text-gray-300">
                    <span className="mr-2 text-white">•</span>
                    {t('privacy:section_03_list_4')}
                  </li>
                </ul>
              </section>

              {/* Section 4 */}
              <section className="space-y-4">
                <h2 className="text-2xl font-bold text-white">
                  {t('privacy:section_04_title')}
                </h2>
                <p className="leading-relaxed text-gray-300">
                  {t('privacy:section_04_intro')}
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start text-gray-300">
                    <span className="mr-2 text-white">•</span>
                    {t('privacy:section_04_list_0')}
                  </li>
                  <li className="flex items-start text-gray-300">
                    <span className="mr-2 text-white">•</span>
                    {t('privacy:section_04_list_1')}
                  </li>
                  <li className="flex items-start text-gray-300">
                    <span className="mr-2 text-white">•</span>
                    {t('privacy:section_04_list_2')}
                  </li>
                  <li className="flex items-start text-gray-300">
                    <span className="mr-2 text-white">•</span>
                    {t('privacy:section_04_list_3')}
                  </li>
                  <li className="flex items-start text-gray-300">
                    <span className="mr-2 text-white">•</span>
                    {t('privacy:section_04_list_4')}
                  </li>
                </ul>
              </section>

              {/* Section 5 */}
              <section className="space-y-4">
                <h2 className="text-2xl font-bold text-white">
                  {t('privacy:section_05_title')}
                </h2>
                <p className="leading-relaxed text-gray-300">
                  {t('privacy:section_05_intro')}
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start text-gray-300">
                    <span className="mr-2 text-white">•</span>
                    {t('privacy:section_05_list_0')}
                  </li>
                  <li className="flex items-start text-gray-300">
                    <span className="mr-2 text-white">•</span>
                    {t('privacy:section_05_list_1')}
                  </li>
                  <li className="flex items-start text-gray-300">
                    <span className="mr-2 text-white">•</span>
                    {t('privacy:section_05_list_2')}
                  </li>
                  <li className="flex items-start text-gray-300">
                    <span className="mr-2 text-white">•</span>
                    {t('privacy:section_05_list_3')}
                  </li>
                </ul>
              </section>

              {/* Section 6 */}
              <section className="space-y-4">
                <h2 className="text-2xl font-bold text-white">
                  {t('privacy:section_06_title')}
                </h2>
                <p className="leading-relaxed text-gray-300">
                  {t('privacy:section_06_intro')}
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start text-gray-300">
                    <span className="mr-2 text-white">•</span>
                    {t('privacy:section_06_list_0')}
                  </li>
                  <li className="flex items-start text-gray-300">
                    <span className="mr-2 text-white">•</span>
                    {t('privacy:section_06_list_1')}
                  </li>
                  <li className="flex items-start text-gray-300">
                    <span className="mr-2 text-white">•</span>
                    {t('privacy:section_06_list_2')}
                  </li>
                  <li className="flex items-start text-gray-300">
                    <span className="mr-2 text-white">•</span>
                    {t('privacy:section_06_list_3')}
                  </li>
                  <li className="flex items-start text-gray-300">
                    <span className="mr-2 text-white">•</span>
                    {t('privacy:section_06_list_4')}
                  </li>
                </ul>
              </section>

              {/* Section 7 */}
              <section className="space-y-4">
                <h2 className="text-2xl font-bold text-white">
                  {t('privacy:section_07_title')}
                </h2>
                <p className="leading-relaxed text-gray-300">
                  {t('privacy:section_07_intro')}
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start text-gray-300">
                    <span className="mr-2 text-white">•</span>
                    {t('privacy:section_07_list_0')}
                  </li>
                  <li className="flex items-start text-gray-300">
                    <span className="mr-2 text-white">•</span>
                    {t('privacy:section_07_list_1')}
                  </li>
                  <li className="flex items-start text-gray-300">
                    <span className="mr-2 text-white">•</span>
                    {t('privacy:section_07_list_2')}
                  </li>
                  <li className="flex items-start text-gray-300">
                    <span className="mr-2 text-white">•</span>
                    {t('privacy:section_07_list_3')}
                  </li>
                  <li className="flex items-start text-gray-300">
                    <span className="mr-2 text-white">•</span>
                    {t('privacy:section_07_list_4')}
                  </li>
                  <li className="flex items-start text-gray-300">
                    <span className="mr-2 text-white">•</span>
                    {t('privacy:section_07_list_5')}
                  </li>
                </ul>
              </section>

              {/* Section 8 */}
              <section className="space-y-4">
                <h2 className="text-2xl font-bold text-white">
                  {t('privacy:section_08_title')}
                </h2>
                <p className="leading-relaxed text-gray-300">
                  {t('privacy:section_08_intro')}
                </p>
              </section>

              {/* Section 9 */}
              <section className="space-y-4">
                <h2 className="text-2xl font-bold text-white">
                  {t('privacy:section_09_title')}
                </h2>
                <p className="leading-relaxed text-gray-300">
                  {t('privacy:section_09_intro')}
                </p>
              </section>

              {/* Section 10 */}
              <section className="space-y-4">
                <h2 className="text-2xl font-bold text-white">
                  {t('privacy:section_10_title')}
                </h2>
                <p className="leading-relaxed text-gray-300">
                  {t('privacy:section_10_intro')}
                </p>
              </section>

              {/* Section 11 */}
              <section className="space-y-4">
                <h2 className="text-2xl font-bold text-white">
                  {t('privacy:section_11_title')}
                </h2>
                <p className="leading-relaxed text-gray-300">
                  {t('privacy:section_11_intro')}
                </p>
              </section>

              {/* Section 12 */}
              <section className="space-y-4">
                <h2 className="text-2xl font-bold text-white">
                  {t('privacy:section_12_title')}
                </h2>
                <p className="leading-relaxed text-gray-300">
                  {t('privacy:section_12_intro')}
                </p>
                <p className="text-blue-400">
                  {t('privacy:contactEmail')}:{' '}
                  <a
                    href="mailto:nookai@embira.co"
                    className="underline text-blue-400 hover:text-blue-300"
                  >
                    nookai@embira.co
                  </a>
                </p>
              </section>
            </div>
          </div>
        </div>
      </MainLayout>
    </>
  );
};
